import { AfterViewInit, Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ProjectVersion } from 'src/app/constants/project-version-constans';
import { FormatDetailModel } from 'src/app/models/formats/format-detail.model';
import { FormatModel } from 'src/app/models/formats/format.model';
import { MovementDetailModel } from 'src/app/models/invoicing/movement-detail.model';
import { LiquidationDetailPrintModel } from 'src/app/models/print/subrental/liquidation-detail-print.model';
import { LiquidationPrintModel } from 'src/app/models/print/subrental/liquidation-print.model';
import { FormatDetailService } from 'src/app/services/formats/format-detail.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { SubrentalCutoffService } from 'src/app/services/subrental/subrental-cutoff.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { PrintPageSizeService } from 'src/app/services/global-services/print-page-size.service';

interface GroupMetadata {
  totalValue: number;
  conceptName: string;
}

@Component({
  selector: 'app-preview-liquidation',
  templateUrl: './preview-liquidation.component.html',
  styleUrls: ['./preview-liquidation.component.scss'],
  standalone: false
})
export class PreviewLiquidationComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  private readonly formatDetailService = inject(FormatDetailService);
  private readonly subrentalCutoffService = inject(SubrentalCutoffService);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly userAuthenticatedService = inject(UserAuthenticatedService);
  private readonly printPageSizeService = inject(PrintPageSizeService);

  @Input() companyId: number | null = 0;
  @Input() editFormat: boolean = false;
  @Input() exportFormat: boolean = false;
  @Input() exporterDetailsPreview: any;
  @Input() focusText: boolean = false;
  @Input() formatDetail: FormatDetailModel = new FormatDetailModel();
  @Input() formatModel: FormatModel = new FormatModel();
  @Input() liquidationPrintModel: LiquidationPrintModel[] = [];
  @Output() emitFormatDetail = new EventEmitter<FormatDetailModel>();
  @Output() filtersPreview = new EventEmitter<any[]>();
  @Output() finishLoadingPrint = new EventEmitter<boolean>();

  bottomPreview: number = 0;
  dateNow: Date = new Date();
  // Tabla Nueva
  groupDataMap: { [key: string]: GroupMetadata } = {};
  columns: { field: string; header: string; }[] = [];
  idMap: { [key: string]: { id: string; type: string; showTotal: boolean; printFormat: any; } } = {
    'articleCodeName': { id: 'articulo', type: 'text', showTotal: false, printFormat: null },
    'document': { id: 'documento', type: 'text', showTotal: false, printFormat: null },
    'dateDocument': { id: 'fecha-documento', type: 'date', showTotal: false, printFormat: null },
    'quantity': { id: 'cantidad', type: 'decimal', showTotal: false, printFormat: null },
    'residue': { id: 'saldo', type: 'decimal', showTotal: false, printFormat: null },
    'totalTime': { id: 'dias', type: 'decimal', showTotal: false, printFormat: null },
    'unitValue': { id: 'valor-unitario', type: 'decimal', showTotal: false, printFormat: null },
    'partialValue': { id: 'valor-parcial', type: 'decimal', showTotal: false, printFormat: null },
  };

  element: any;
  elementStyle: any;
  elementsBody: FormatDetailModel[] = [];
  elementsLabel: FormatDetailModel[] = [];
  formatModelByCompany: FormatModel = new FormatModel();
  generalParametersId: Array<number> = new Array<number>();
  id = '';
  isShift = false;
  leftPreview: number = 0;
  listFormatDetailUpdates: Array<FormatDetailModel> = new Array<FormatDetailModel>();
  listStore: any[] = [];
  movementDetails: Array<MovementDetailModel> = new Array<MovementDetailModel>();
  subrentalMovementId: number = 0;
  mouseDown: boolean = false;
  newFormatDetail: FormatDetailModel = new FormatDetailModel();
  rightPreview: number = 0;
  rows: LiquidationDetailPrintModel[] = [];
  shiftX: number = 0;
  shiftY: number = 0;
  speed = 2;
  store: FormControl = new FormControl();
  storeId: number | null = null;
  topPreview: number = 0;
  userAuthenticated: string = '';
  version: string = ProjectVersion.projectVersion;
  visibleLoading: boolean = true;

  private keydownListener!: (e: KeyboardEvent) => void;
  private mouseupListener!: () => void;
  private mousemoveListener!: (e: MouseEvent) => void;
  private keyupListener!: (e: KeyboardEvent) => void;

  async ngOnInit() {
    await this.getAllLists();
    this.getUserAutheticated();
  }

  async ngOnChanges(changes: SimpleChanges) {
    const {
      exportFormat,
      liquidationPrintModel,
      formatDetail,
      exporterDetailsPreview,
      formatModel,
    } = changes;

    if (exportFormat && !exportFormat.firstChange) {
      this.updateRangeFormatDetail();
    }
    if (formatModel && formatModel.firstChange) {
      this.formatModel = formatModel.currentValue;
    }

    if (liquidationPrintModel && !liquidationPrintModel.firstChange) {
      this.visibleLoading = true;
      if (this.liquidationPrintModel.length) {
        await Promise.all([
          this.configureFormatByDocument(),
        ]);
      } else {
        this.visibleLoading = false;
      }
    }

    if (formatDetail && !formatDetail.firstChange) this.changeFormatDetail();
    if (exporterDetailsPreview?.previousValue !== undefined)
      this.exportPreinvoices();
  }

  ngOnDestroy() {
    this.printPageSizeService.removePageSizeStyle();
    this.clearFilters();

    // Remover event listeners globales
    if (this.keydownListener) document.removeEventListener('keydown', this.keydownListener);
    if (this.mouseupListener) document.removeEventListener('mouseup', this.mouseupListener, true);
    if (this.mousemoveListener) document.removeEventListener('mousemove', this.mousemoveListener, true);
    if (this.keyupListener) document.removeEventListener('keyup', this.keyupListener);
  }

  ngAfterViewInit() {
    if (this.editFormat) {
      this.keydownListener = (e: KeyboardEvent) => {
        const target = e.target as HTMLElement;
        if (
          target &&
          (target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.tagName === 'SELECT' ||
            target.isContentEditable)
        ) {
          return;
        }
        // 2. Si no hay ningún elemento seleccionado para mover, no bloquear
        if (!this.element) {
          return;
        }

        // Deshabilita desplazamiento del scroll con la tecla espacio y flechas
        if (["Space", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].indexOf(e.code) > -1) {
          this.blockScrollArrows(e);
        }

        if (e.which == 16) this.isShift = true;
        //Mover element con flechas
        if (!this.isShift) {
          switch (e.key) {
            case 'ArrowLeft': { this.moveLeft(); break; }
            case 'ArrowRight': { this.moveRight(); break; }
            case 'ArrowUp': { this.moveUp(); break; }
            case 'ArrowDown': { this.moveDown(); break; }
          }
        }

        //Modificar element tamaño de objeto
        if (this.isShift) {
          switch (e.which) {
            case 37: { this.decreaseWidth(); break; }
            case 38: { this.decreaseHeight(); break; }
            case 39: { this.increaseWidth(); break; }
            case 40: { this.increaseHeight(); break; }
          }
        }
      };

      this.mouseupListener = () => {
        this.onMouseUp();
      };

      this.mousemoveListener = (e: MouseEvent) => {
        this.onMouseMove(e);
      };

      this.keyupListener = (e: KeyboardEvent) => {
        if (e.which == 16) this.isShift = false;
      };

      // Registrar los listeners en el document
      document.addEventListener('keydown', this.keydownListener);
      document.addEventListener('mouseup', this.mouseupListener, true);
      document.addEventListener('mousemove', this.mousemoveListener, true);
      document.addEventListener('keyup', this.keyupListener);
    }
  }

  blockScrollArrows(e: any) {
    if (!this.focusText) {
      e.preventDefault();
    }
  }

  async changeAlignment(alignment: string) {
    this.element.style.textAlign = alignment;
  }

  changeBold(bold: string) {
    this.element.style.fontWeight = bold;
  }

  changeBorder(border: string) {
    switch (border) {
      case 'noborder': {
        this.element.style.border = '0px none rgb(0, 0, 0)';
        break;
      }
      case 'border': {
        this.element.style.border = '1px solid rgb(0, 0, 0)';
        break;
      }
      case 'underline': {
        this.element.style.border = '0px none rgb(0, 0, 0)';
        this.element.style.borderBottom = '1px solid rgb(0, 0, 0)';
        break;
      }
    }
  }

  changeFormatDetail() {
    const formatDetailId = this.formatDetail.formatDetailId;
    if (formatDetailId == 0) return;
    const isBody = this.formatDetail.band === 'body';
    const isLabel = this.formatDetail.type === 'label';

    if (isBody) {
      const element = this.elementsBody.find((x) => x.formatDetailId === formatDetailId);
      if (!element) {
        return;
      }

      element.alignment = this.formatDetail.alignment;
      element.fontSize = this.formatDetail.fontSize;
      element.printFormat = this.formatDetail.printFormat;

      if (isLabel) {
        element.visible = this.formatDetail.visible;
        element.width = this.formatDetail.width;
        element.text = this.formatDetail.text;
        if (!this.formatDetail.visible) {
          this.columns = this.columns.filter(x => x.field !== element.description);
        } else {
          this.updateElementWidth(this.formatDetail.name, this.liquidationPrintModel[0].subrentalMovementId!);
          this.updateElementStyle(this.formatDetail.name, this.liquidationPrintModel[0].subrentalMovementId);
          this.changeAlignment(this.formatDetail.alignment);
        }
      } else {
        element.bold = this.formatDetail.bold;
        this.liquidationPrintModel[0].liquidationDetails.forEach((detail) => {
          if (detail.subrentalMovementDetailId) {
            this.updateElementStyle(this.formatDetail.name, detail.subrentalMovementDetailId);
            this.changeAlignment(this.formatDetail.alignment);
            this.changeBold(this.formatDetail.bold);
          }
        });
      }
      this.elementsBody = this.elementsBody.filter((x) => x.formatDetailId !== formatDetailId);
      this.elementsBody.push(element);
      const fieldName = element.description;
      if (fieldName && this.idMap[fieldName]) {
        this.idMap[fieldName].printFormat = element.printFormat;
      }
    } else {
      if (this.formatDetail.name.includes('foot-group')) {
        this.groupingClass(this.formatDetail);
      } else {
        this.updateElementStyle(this.formatDetail.name, this.liquidationPrintModel[0].subrentalMovementId);
        if (!this.formatDetail.visible) {
          this.element.setAttribute('hidden', '');
        } else {
          this.applyFormatStyles(this.element);
          this.updateLabelText(formatDetailId, this.formatDetail.text!);
          this.listFormatDetailUpdates = this.listFormatDetailUpdates.filter((x) => x.formatDetailId !== formatDetailId);
          this.listFormatDetailUpdates.push(this.formatDetail);
        }
      }
      const labelElement = this.elementsLabel.find(x => x.name === this.formatDetail.name);
      if (labelElement) {
        labelElement.printFormat = this.formatDetail.printFormat;
        this.elementsLabel = [...this.elementsLabel];
      }
    }
  }

  private updateElementStyle(name: string, id: string | number) {
    this.element = document.getElementById(`${name}-${id}`);
    if (!this.element) {
      return;
    }

    this.element.style.fontSize = `${this.formatDetail.fontSize}px`;
  }

  private updateElementWidth(name: string, id: string | number) {
    this.element = document.getElementById(`${name}-${id}`);
    if (!this.element) {
      return;
    }
    this.element.style.width = `${this.formatDetail.width}%`;
  }

  private applyFormatStyles(element: HTMLElement) {
    element.style.fontSize = `${this.formatDetail.fontSize}px`;
    this.changeAlignment(this.formatDetail.alignment);
    this.changeBold(this.formatDetail.bold);
    this.changeBorder(this.formatDetail.border);

    if (!this.formatDetail.name.includes('foot-')) {
      element.style.position = 'absolute';
      element.style.left = `${this.formatDetail.left}%`;
      element.style.top = `${this.formatDetail.top}%`;
    }

    element.style.height = `${this.formatDetail.height}px`;
    element.style.width = `${this.formatDetail.width}px`;
  }

  private updateLabelText(formatId: number, text: string) {
    const labelElement = this.elementsLabel.find((x) => x.formatDetailId === formatId);
    if (labelElement) {
      labelElement.text = text;
    }
  }

  async chargeFormat(formatModel: FormatModel): Promise<boolean> {
    return new Promise((resolved) => {
      this.formatModel = formatModel;
      this.printPageSizeService.setPageSizeStyle(formatModel.paperSizeId);

      this.liquidationPrintModel.forEach((liquidation) => {
        setTimeout(async () => {
          this.applyHeaderFooterHeights(liquidation.subrentalMovementId, formatModel);
          this.extractFormatDetails(formatModel);
          this.buildDisplayedColumns();
          this.visibleLoading = true;
          setTimeout(() => {
            this.applyFormatDetails(formatModel.formatDetails, liquidation);
            this.applyColumnStyles(liquidation);
            this.applyParagraphStyles(liquidation);
            resolved(true);
          }, 200);
        }, 200);
      });
    });
  }

  private applyHeaderFooterHeights(subrentalMovementId: number, formatModel: FormatModel) {
    const head = document.getElementById(`head-${subrentalMovementId}`);
    head!.style.height = `${formatModel.head}px`;
  }

  private extractFormatDetails(formatModel: FormatModel) {
    this.elementsLabel = formatModel.formatDetails.filter((x) => x.type === 'label');
    const copyFormatModel: FormatModel = JSON.parse(JSON.stringify(formatModel));
    copyFormatModel.formatDetails.forEach((element) => {
      if (!element) {
        return;
      }
    });

    this.elementsBody = copyFormatModel.formatDetails.filter((x) => x.band === 'body' && x.visible).sort((a, b) => a.orderTable - b.orderTable);
  }

  private buildDisplayedColumns() {
    this.columns = [];
    this.elementsBody.filter((x) => x.type === 'label').forEach((element) => {
      this.columns.push({ field: element.description!, header: element.text! });
      this.idMap[element.description!].printFormat = element.printFormat ?? null;
    });
  }

  private async applyFormatDetails(elements: FormatDetailModel[], liquidation: LiquidationPrintModel) {
    for (const element of elements) {
      if (element.band === 'body') continue;
      if (element.name.includes('foot-group')) {
        setTimeout(() => { this.groupingClass(element); }, 0);
      } else {
        this.element = document.getElementById(`${element.name}-${liquidation.subrentalMovementId}`);
        if (this.element) {
          this.applyElementStyles(element);
        }
      }
    }
  }

  private applyElementStyles(element: FormatDetailModel) {
    if (!element.visible) {
      this.element!.setAttribute('hidden', '');
      return;
    }

    const style = this.element!.style;
    style.fontSize = `${element.fontSize}px`;
    this.changeAlignment(element.alignment);
    this.changeBold(element.bold);
    this.changeBorder(element.border);

    if (element.name.includes('foot-')) {
      style.height = `${element.height}px`;
      style.width = `${element.width}px`;
    } else {
      style.backgroundColor = 'transparent';
      style.position = 'absolute';
      style.left = `${element.left}%`;
      style.top = `${element.top}%`;
      style.height = `${element.height}px`;
      style.width = `${element.width}px`;
    }
  }

  private async applyColumnStyles(liquidation: LiquidationPrintModel) {
    for (const column of this.columns.map(x => x.field)) {
      const element = this.elementsBody.find((x) => x.description === column);
      if (!element) continue;

      this.element = document.getElementById(`${element.name}-${liquidation.subrentalMovementId}`);
      if (this.element) {
        this.element.style.fontSize = `${element.fontSize}px`;
        this.element.style.width = `${element.width}%`;
        await this.changeAlignment(element.alignment);
      }
    }
  }

  private applyParagraphStyles(liquidation: LiquidationPrintModel) {
    this.elementsBody.filter((x) => x.type === 'p')
      .forEach((element) => {
        liquidation.liquidationDetails.forEach((detail) => {
          this.element = document.getElementById(`${element.name}-${detail.subrentalMovementDetailId}`);
          if (this.element) {
            this.element.style.fontSize = `${element.fontSize}px`;
            this.changeAlignment(element.alignment);
            this.changeBold(element.bold);
          }
        });
      });
  }

  clickTable(event: any) {
    if (!this.editFormat) {
      return;
    }

    const element = document.getElementById(event.target.id);
    if (!element) {
      return;
    }

    const elementStyle = window.getComputedStyle(element);
    const name = event.target.id.substring(0, event.target.id.lastIndexOf('-'));
    const formatDetail = this.formatModel.formatDetails.find((x) => x.name === name);
    const bodyElement = this.elementsBody.find((x) => x.name === name);

    if (!formatDetail || !bodyElement) {
      return;
    }

    this.newFormatDetail = new FormatDetailModel();
    this.newFormatDetail.name = name;
    this.newFormatDetail.formatDetailId = formatDetail.formatDetailId;
    this.newFormatDetail.formatId = this.formatModel.formatId;
    this.newFormatDetail.description = formatDetail.description;
    this.newFormatDetail.band = formatDetail.band;
    this.newFormatDetail.type = formatDetail.type;
    this.newFormatDetail.visible = bodyElement.visible;
    this.newFormatDetail.text = bodyElement.text;
    this.newFormatDetail.orderTable = bodyElement.orderTable;
    this.newFormatDetail.printFormat = bodyElement.printFormat;

    // Ancho
    if (formatDetail.type === 'label') {
      const rawWidth = element.style.width;
      const width = parseFloat(rawWidth.replace('%', '')) || 0;
      this.newFormatDetail.width = parseFloat(width.toFixed(4));
    } else {
      this.newFormatDetail.width = 0;
    }

    // Tamaño de fuente
    const fontSize = parseInt(elementStyle.getPropertyValue('font-size').replace('px', ''));
    this.newFormatDetail.fontSize = isNaN(fontSize) ? 0 : fontSize;

    // Negrita
    const fontWeight = elementStyle.getPropertyValue('font-weight');
    this.newFormatDetail.bold = fontWeight === '400' ? 'normal' : 'bold';

    // Alineación
    this.newFormatDetail.alignment = elementStyle.getPropertyValue('text-align');
    this.emitFormatDetail.emit(this.newFormatDetail);
  }

  private adjustElementSize(property: 'height' | 'width', delta: number) {
    if (this.focusText) {
      return;
    }

    const currentValue = this.elementStyle.getPropertyValue(property);
    const numericValue = parseFloat(currentValue.replace('px', ''));
    const newValue = numericValue + delta;
    this.element.style[property] = `${newValue}px`;
    this.onClick();
  }

  decreaseHeight() {
    this.adjustElementSize('height', -this.speed);
  }

  increaseHeight() {
    this.adjustElementSize('height', this.speed);
  }

  decreaseWidth() {
    this.adjustElementSize('width', -this.speed);
  }

  increaseWidth() {
    this.adjustElementSize('width', this.speed);
  }

  onColumnReorder() {
    this.columns.forEach((col, index) => {
      this.elementsBody.find(x => x.description == col.field)!.orderTable = index;
    });
  }

  async configureFormatByDocument() {
    try {
      const promises = this.liquidationPrintModel.flatMap(liquidation => this.processLiquidation(liquidation));

      await Promise.all(promises);
      await this.chargeFormat(this.formatModel);
      this.dateNow = new Date();
      this.visibleLoading = false;
      this.finishLoadingPrint.emit(true);
    } catch (error) {
      console.error("Error en la configuración de factura:", error);
    }
  }

  async exportPreinvoices() {
    const details = this.liquidationPrintModel?.[0]?.liquidationDetails;
    if (!details || details.length === 0) {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para exportar.'
      );
      return;
    }
    try {
      const storeId = this.store.value ?? 0;
      const subrentalCutoffId = this.liquidationPrintModel[0]?.subrentalCutoffId ?? 0;
      const blob = await firstValueFrom(this.subrentalCutoffService.exportSubrentalPreinvoices(subrentalCutoffId, storeId));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'VistaPreviaDetalles.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar:', error);
    }
  }

  private processLiquidation(liquidation: LiquidationPrintModel): Promise<void>[] {
    const promises: Promise<void>[] = [];

    // 3. Ordenar y Re-indexar detalles por concepto
    liquidation.liquidationDetails
      .sort((a, b) => a.conceptInvoicingId - b.conceptInvoicingId)
      .forEach((detail, index) => (detail.index = index + 1));

    // 4. Procesar Grupos si aplica
    if (this.formatModel.group) {
      const conceptPromises = this.getGroupPromises(liquidation);
      promises.push(...conceptPromises);
    }

    return promises;
  }

  private getGroupPromises(liquidation: LiquidationPrintModel): Promise<void>[] {
    const uniqueConceptIds = [...new Set(liquidation.liquidationDetails.map(d => d.conceptInvoicingId))];

    return uniqueConceptIds.map(async (conceptId) => {
      const groupItems = liquidation.liquidationDetails.filter(d => d.conceptInvoicingId === conceptId);
      const firstItem = groupItems[0];
      const totalValueGroup = groupItems.reduce((sum, item) => sum + (item.partialValue || 0), 0);


      this.groupDataMap[`${liquidation.subrentalMovementId}_${conceptId}`] = {
        totalValue: totalValueGroup,
        conceptName: firstItem.codeNameConceptInvoicing
      };
    });
  }

  getLabelPrintFormat(name: string): string | null {
    return this.elementsLabel.find(x => x.name === name)?.printFormat ?? null;
  }

  getGroupInfo(subrentalMovementId: number, conceptId: number) {
    return this.groupDataMap[`${subrentalMovementId}_${conceptId}`];
  }

  async getStore(storeId: number | null) {
    this.storeId = storeId;
    this.updateFilters();
  }

  async groupingClass(element: FormatDetailModel) {
    const classMap: { [key: string]: string } = {
      'foot-group-concept': '.group-concept'
    };

    const selector = classMap[element.name];
    if (selector) {
      const classEdit = document.querySelectorAll(selector);
      await this.setGroupingStyles(element, classEdit);
    }
  }

  getUserAutheticated() {
    return new Promise((resolve) => {
      this.userAuthenticatedService.userObservable.subscribe((value) => { this.userAuthenticated = value.userName; });
      resolve(true);
    });
  }

  async getAllLists() {
    try {
      const [stores] = await Promise.all([
        firstValueFrom(this.subrentalCutoffService.getAllStoreForLiquidationSelectAsync()),
      ]);
      this.listStore = stores;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas.', error);
      return false;
    }
  }

  private moveElement(direction: 'top' | 'left', delta: number, previewLimit: number, isVertical: boolean) {
    if (this.focusText || !this.elementStyle) {
      return;
    }

    const currentValue = this.elementStyle.getPropertyValue(direction);
    let position = parseFloat(currentValue.replace('px', ''));
    position += delta;
    const sizeProp = isVertical ? 'height' : 'width';
    const size = parseFloat(this.elementStyle.getPropertyValue(sizeProp).replace('px', ''));

    // Restringir dentro del contenedor
    if (position < 0) position = 0;
    if (position + size > previewLimit) position = previewLimit - size;
    const percentage = ((position / previewLimit) * 100).toFixed(4);
    this.element.style[direction] = `${percentage}%`;

    this.onClick();
  }

  moveUp() {
    this.moveElement('top', -this.speed, this.bottomPreview, true);
  }

  moveDown() {
    this.moveElement('top', this.speed, this.bottomPreview, true);
  }

  moveLeft() {
    this.moveElement('left', -this.speed, this.rightPreview, false);
  }

  moveRight() {
    this.moveElement('left', this.speed, this.rightPreview, false);
  }

  onClick() {
    if (!this.editFormat) {
      return;
    }

    const name = this.id.substring(0, this.id.lastIndexOf('-'));
    const formatDetail = this.formatModel.formatDetails.find((x) => x.name === name);
    if (!formatDetail) {
      return;
    }

    this.newFormatDetail = new FormatDetailModel();
    this.newFormatDetail.name = name;
    this.newFormatDetail.formatDetailId = formatDetail.formatDetailId;
    this.newFormatDetail.formatId = this.formatModel.formatId;
    this.newFormatDetail.band = formatDetail.band;
    this.newFormatDetail.description = formatDetail.description;
    this.newFormatDetail.text = formatDetail.text;
    this.newFormatDetail.visible = true;
    this.newFormatDetail.printFormat = formatDetail.printFormat;

    this.newFormatDetail.fontSize = this.getStylePxValue('font-size');
    this.newFormatDetail.bold = this.elementStyle.getPropertyValue('font-weight') === '400' ? 'normal' : 'bold';
    this.newFormatDetail.alignment = this.elementStyle.getPropertyValue('text-align');
    this.newFormatDetail.border = this.getBorderStyle();

    if (!name.includes('foot-')) {
      this.newFormatDetail.left = parseFloat(((this.getStylePxValue('left') / this.rightPreview) * 100).toFixed(4));
      this.newFormatDetail.top = parseFloat(((this.getStylePxValue('top') / this.bottomPreview) * 100).toFixed(4));
    }

    this.newFormatDetail.width = parseFloat(this.getStylePxValue('width').toFixed(4));
    this.newFormatDetail.height = parseFloat(this.getStylePxValue('height').toFixed(4));

    this.emitFormatDetail.emit(this.newFormatDetail);
    this.listFormatDetailUpdates = this.listFormatDetailUpdates.filter((x) => x.formatDetailId !== this.newFormatDetail.formatDetailId);
    this.listFormatDetailUpdates.push(this.newFormatDetail);
  }

  private getStylePxValue(property: string): number {
    const value = this.elementStyle.getPropertyValue(property);
    return parseFloat(value.replace('px', '')) || 0;
  }

  private getBorderStyle(): string {
    if (!this.elementStyle) return 'noborder';

    const sides = ['top', 'right', 'bottom', 'left'] as const;

    const hasBorder = (side: (typeof sides)[number]) => {
      const width = this.elementStyle.getPropertyValue(`border-${side}-width`);
      const style = this.elementStyle.getPropertyValue(`border-${side}-style`);
      return style !== 'none' && width !== '0px';
    };

    const borderStates = sides.map((side) => hasBorder(side));
    const [top, right, bottom, left] = borderStates;

    if (top || right || left) {
      return 'border';
    } else if (bottom) {
      return 'underline';
    } else {
      return 'noborder';
    }
  }

  onDragStart() {
    return false;
  }

  onMouseDown(event: any) {
    if (!this.editFormat) {
      return;
    }

    if (this.element) this.element.style.backgroundColor = 'transparent';

    this.mouseDown = true;
    this.id = event.target.id;
    this.element = document.getElementById(this.id);
    if (!this.element) {
      return;
    }

    this.elementStyle = window.getComputedStyle(this.element);
    this.element.style.backgroundColor = '#0d6efd6e';
    const name = this.id.substring(0, this.id.lastIndexOf('-'));
    const formatDetail = this.formatModel.formatDetails.find((x) => x.name === name);
    if (!formatDetail) {
      return;
    }

    const relativeElement = document.getElementById(`${formatDetail.band}-${this.liquidationPrintModel[0].subrentalMovementId}`);
    if (!relativeElement) {
      return;
    }

    const relativeStyle = window.getComputedStyle(relativeElement);
    this.leftPreview = Math.round(relativeElement.offsetLeft - relativeElement.scrollLeft);
    this.topPreview = Math.round(relativeElement.offsetTop - relativeElement.scrollTop);
    this.rightPreview = parseInt(relativeStyle.width.replace('px', ''));
    this.bottomPreview = parseInt(relativeStyle.height.replace('px', ''));

    if (!this.id.includes('foot-')) {
      this.element.style.cursor = 'move';
      const leftPX = event.pageX - this.leftPreview;
      const topPX = event.pageY - this.topPreview;
      this.shiftX = leftPX - this.getStylePx('left');
      this.shiftY = topPX - this.getStylePx('top');
    }

    this.onClick();
  }

  onMouseMove(event: any) {
    if (!this.mouseDown || this.id.includes('foot-')) { return; }
    this.element.style.position = 'absolute';
    const leftPx = Math.max(0, event.pageX - this.leftPreview - this.shiftX);
    const widthPx = this.getStylePx('width');
    const finalLeft = Math.min(leftPx, this.rightPreview - widthPx);
    const topPx = Math.max(0, event.pageY - this.topPreview - this.shiftY);
    const heightPx = this.getStylePx('height');
    const finalTop = Math.min(topPx, this.bottomPreview - heightPx);
    this.updateElementPosition(finalLeft, finalTop);
    this.onClick();
  }

  private getStylePx(property: string): number {
    return (parseFloat(this.elementStyle.getPropertyValue(property).replace('px', '')) || 0);
  }

  private updateElementPosition(leftPx: number, topPx: number) {
    const leftPercentage = ((leftPx / this.rightPreview) * 100).toFixed(4);
    const topPercentage = ((topPx / this.bottomPreview) * 100).toFixed(4);
    this.element.style.left = `${leftPercentage}%`;
    this.element.style.top = `${topPercentage}%`;
  }

  clearFilters() {
    this.storeId = null;
    this.store.patchValue(null);
    this.updateFilters();
  }

  updateFilters() {
    const filters = [{ label: 'storeId', value: this.storeId }];
    this.filtersPreview.emit(filters);
  }

  onMouseUp() {
    this.mouseDown = false;
  }

  async setGroupingStyles(element: FormatDetailModel, classEdit: any) {
    let border: string, borderBottom: string;
    switch (element.border) {
      case 'noborder': {
        border = '0px none rgb(0, 0, 0)';
        borderBottom = '';
        break;
      }
      case 'border': {
        border = '1px solid rgb(0, 0, 0)';
        borderBottom = '';
        break;
      }
      case 'underline': {
        border = '0px none rgb(0, 0, 0)';
        borderBottom = '1px solid rgb(0, 0, 0)';
        break;
      }
    }

    classEdit.forEach((el: HTMLElement) => {
      el.style.backgroundColor = 'transparent';
      el.style.fontSize = `${element.fontSize}px`;
      el.style.textAlign = element.alignment;
      el.style.fontWeight = element.bold;
      el.style.border = border;
      el.style.borderBottom = borderBottom;
      el.style.height = `${element.height}px`;
      el.style.width = `${element.width}px`;
    });
  }

  async updateRangeFormatDetail() {
    this.elementsBody.forEach((element) => { this.listFormatDetailUpdates.push(element); });
    const response = await firstValueFrom(this.formatDetailService.updateRangeFormatDetail(this.listFormatDetailUpdates));
    this.chargeFormat(response);
  }

}
